
import { computed, defineComponent, ref } from '@vue/composition-api'

import {
  mdiAccount,
  mdiEarth,
  mdiLogin,
  mdiLogout,
  mdiThemeLightDark,
  mdiTune,
  mdiWeatherNight,
  mdiWeatherSunny,
  mdiCommentOutline,
} from '@mdi/js'
import { useAuthActions, useAuthGetters, useLocaleActions } from '@/store'

import CommonFlagIcon from '@/components/common/common-flag-icon.vue'

import { resolveCountry } from '@/plugins/i18n'

type Theme = 'dark' | 'light' | 'auto'

export default defineComponent({
  name: 'app-bar',
  components: {
    CommonFlagIcon,
  },
  props: {
    isNavOpen: {
      type: Boolean,
      required: false,
    },
  },
  setup: (_, { root }) => {
    const { isAuthenticated } = useAuthGetters()

    const { setCurrentLocale } = useLocaleActions()

    const currentLocale = computed(() => root.$store.state.locale.currentLocale)
    const implementetLocales = computed<string[]>(() => root.$store.state.locale.implementedLocales)

    const theme = ref<Theme>('light')

    const themeButton = computed(() => {
      if (theme.value === 'auto') {
        return { title: 'Theme Auto', icon: mdiThemeLightDark }
      } else if (theme.value === 'light') {
        return { title: 'Theme Light', icon: mdiWeatherSunny }
      } else {
        return { title: 'Theme Dark', icon: mdiWeatherNight }
      }
    })

    const prefersDark = ref(window.matchMedia('(prefers-color-scheme: dark)').matches)

    const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    darkModeMediaQuery.addListener((e) => {
      prefersDark.value = e.matches
    })

    const themeAuto = () => {
      localStorage.removeItem('theme')
      theme.value = 'auto'
      if (prefersDark.value) {
        root.$vuetify.theme.dark = true
      } else {
        root.$vuetify.theme.dark = false
      }
    }

    const themeDark = () => {
      theme.value = 'dark'
      localStorage.setItem('theme', 'dark')
      root.$vuetify.theme.dark = true
    }

    const themeLight = () => {
      theme.value = 'light'
      localStorage.setItem('theme', 'light')
      root.$vuetify.theme.dark = false
    }

    const initTheme = () => {
      const theme = localStorage.getItem('theme')
      if (theme === 'dark') themeDark()
      else if (theme === 'light') themeLight()
      else themeAuto()
    }

    initTheme()

    const toggleTheme = () => {
      if (theme.value === 'auto') themeLight()
      else if (theme.value === 'light') themeDark()
      else if (theme.value === 'dark') themeAuto()
    }

    const { logout } = useAuthActions()

    const title = computed(() => (root.$route.meta?.title ? root.$tc(root.$route.meta?.title, 2) : ''))

    function checkTheme(theme) {
      switch (theme) {
        case 'light':
          return root.$t('misc.lighttheme')

        case 'dark':
          return root.$t('misc.darktheme')

        default:
          root.$t('misc.autotheme')
      }
    }

    return {
      icons: {
        mdiLogin,
        mdiLogout,
        mdiAccount,
        mdiTune,
        mdiEarth,
        mdiCommentOutline,
      },
      isAuthenticated,
      currentLocale,
      setCurrentLocale,
      implementetLocales,
      themeButton,
      theme,
      toggleTheme,
      logout,
      resolveCountry,
      title,
      checkTheme,
    }
  },
})
